import { formatDate, formatDaysLeft, formatMoney } from '@persona/shared';
import { Alert, Input, Skeleton, StatusPill } from '@persona/ui';
import { useEffect, useState } from 'react';
import { adminApi, ApiError, type AdminSubscriptionRow } from '../api';
import { EmptyRow, Th } from '../components/Shell';

/** Статусы подписки на языке сотрудника клуба. */
const STATUS: Record<string, { label: string; tone: 'success' | 'warning' | 'danger' | 'neutral' }> = {
  active: { label: 'Активна', tone: 'success' },
  pending: { label: 'Ждёт оплаты', tone: 'warning' },
  past_due: { label: 'Оплата не прошла', tone: 'danger' },
  canceled: { label: 'Отменена', tone: 'neutral' },
  expired: { label: 'Завершена', tone: 'neutral' },
};

/** Подписки со сроками окончания и датой следующего списания. */
export function SubscriptionsPage({ onOpenClient }: { onOpenClient: (id: string) => void }) {
  const [rows, setRows] = useState<AdminSubscriptionRow[] | null>(null);
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setRows(null);
      adminApi
        .subscriptions(search)
        .then((result) => setRows(result.items))
        .catch((cause: unknown) => {
          setError(cause instanceof ApiError ? cause.message : 'Не удалось загрузить подписки');
          setRows([]);
        });
    }, 300);
    return () => window.clearTimeout(timer);
  }, [search]);

  return (
    <div>
      <h1 className="font-display text-2xl font-semibold tracking-wide uppercase">Подписки</h1>
      <p className="mt-2 text-sm text-secondary">
        Сроки действия и ближайшие списания. Строки отсортированы по дате окончания — сверху те, кто скоро уйдёт.
      </p>

      {error && <Alert tone="danger" className="mt-6">{error}</Alert>}

      <div className="mt-6 max-w-md">
        <Input
          type="search"
          placeholder="Поиск по клиенту"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>

      <div className="mt-4 overflow-x-auto">
        <table className="w-full min-w-[900px] border-collapse text-sm">
          <thead>
            <tr className="border-b border-[var(--border-strong)]">
              <Th>Клиент</Th>
              <Th>Тариф</Th>
              <Th>Статус</Th>
              <Th>Действует до</Th>
              <Th>Автопродление</Th>
              <Th>Следующее списание</Th>
            </tr>
          </thead>
          <tbody>
            {rows === null ? (
              Array.from({ length: 4 }, (_, index) => (
                <tr key={index} className="border-b border-[var(--border-subtle)]">
                  <td className="px-3 py-4" colSpan={6}>
                    <Skeleton className="h-4 w-full" />
                  </td>
                </tr>
              ))
            ) : rows.length === 0 ? (
              <EmptyRow colSpan={6}>
                Подписок пока нет. Они появятся здесь после первой оплаты через кабинет.
              </EmptyRow>
            ) : (
              rows.map((row) => {
                const status = STATUS[row.status] ?? { label: row.status, tone: 'neutral' as const };
                return (
                  <tr key={row.id} className="border-b border-[var(--border-subtle)]">
                    <td className="px-3 py-3">
                      <button
                        type="button"
                        onClick={() => onOpenClient(row.client.id)}
                        className="text-left font-medium text-[var(--accent)] hover:underline"
                      >
                        {row.client.name}
                      </button>
                      <div className="text-xs text-muted">{row.client.phone}</div>
                    </td>
                    <td className="px-3 py-3">{row.tariffTitle}</td>
                    <td className="px-3 py-3">
                      <StatusPill tone={status.tone}>{status.label}</StatusPill>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      {row.currentPeriodEnd ? (
                        <>
                          {formatDate(row.currentPeriodEnd)}
                          {row.daysLeft !== null && (
                            <div className="text-xs text-muted">осталось {formatDaysLeft(row.daysLeft)}</div>
                          )}
                        </>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      {row.autoRenew ? 'Включено' : <span className="text-muted">Отключено</span>}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      {row.nextChargeAt ? (
                        <>
                          {formatDate(row.nextChargeAt)}
                          {row.nextChargeAmountMinor !== null && (
                            <div className="text-xs text-muted">{formatMoney(row.nextChargeAmountMinor)}</div>
                          )}
                        </>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                      {/* Неудачные попытки видно сразу: по ним звонят клиенту */}
                      {row.failedChargeCount > 0 && (
                        <div className="text-xs text-danger-500">неудачных попыток: {row.failedChargeCount}</div>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
